import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus, Eye, ClipboardList, Download } from 'lucide-react'
import {
  Child,
  MedicalHistory,
  fetchChildren,
  fetchMedicalHistories,
  fetchMedicalHistory,
  getMedicalHistoryPdfUrl,
} from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { YearSelector } from '@/components/ui/year-selector'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

export function MedicalHistoryListPage() {
  const { t } = useTranslation(['common', 'medicalHistory'])
  const navigate = useNavigate()
  const { token, parent } = useAuthStore()
  const [medicalHistories, setMedicalHistories] = useState<MedicalHistory[]>([])
  const [children, setChildren] = useState<Child[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedRecord, setSelectedRecord] = useState<MedicalHistory | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !parent?.id) {
        setLoading(false)
        return
      }
      try {
        const [mhData, childrenData] = await Promise.all([
          fetchMedicalHistories(),
          fetchChildren(),
        ])
        setMedicalHistories(mhData || [])
        setChildren(childrenData || [])
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'common' }))
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [token, parent, t])

  const getChildName = (childId: number) => {
    const child = children.find(c => c.id === childId)
    return child ? `${child.first_name} ${child.last_name}` : `Child #${childId}`
  }

  const handleViewClick = (record: MedicalHistory) => {
    setIsModalOpen(true)
    setSelectedRecord(record)
    fetchMedicalHistory(record.id)
      .then(fullRecord => setSelectedRecord(fullRecord))
      .catch(() => setError(t('errors.loadFailed', { ns: 'common' })))
  }

  const handleDownloadPdf = (id: number) => {
    getMedicalHistoryPdfUrl(id)
      .then(url => window.open(url, '_blank'))
      .catch((error) => {
        if (error.message?.includes('still being generated')) {
          alert(t('pdfGenerating', { ns: 'medicalHistory', defaultValue: 'PDF is being generated. Please try again in a few seconds.' }))
        } else {
          alert(t('pdfDownloadError', { ns: 'medicalHistory', defaultValue: 'Error downloading PDF. Please try again later.' }))
        }
      })
  }

  const renderYesNo = (value: boolean) =>
    value ? (
      <span className="text-emerald-700 font-medium">{t('yes', { ns: 'medicalHistory' })}</span>
    ) : (
      <span className="text-destructive font-medium">{t('no', { ns: 'medicalHistory' })}</span>
    )

  const renderArrayData = (data: any) => {
    if (!data) return '-'
    if (Array.isArray(data)) {
      if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'name' in data[0]) {
        return data.map((item: any) => item.name).join(', ')
      }
      return data.join(', ')
    }
    return String(data)
  }

  const renderSection = (title: string, content: React.ReactNode) => (
    <div className="mb-3 rounded-lg border overflow-hidden">
      <div className="bg-muted px-4 py-2 border-b">
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="p-4 text-sm">{content}</div>
    </div>
  )

  // Available years from data
  const availableYears = useMemo(() => {
    return [...new Set(medicalHistories.map(mh => mh.year))].sort((a, b) => b - a)
  }, [medicalHistories])

  // Filter by year
  const filteredHistories = useMemo(() => {
    return medicalHistories.filter(mh => mh.year === selectedYear)
  }, [medicalHistories, selectedYear])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('title', { ns: 'medicalHistory' })}</h1>
        </div>
        <Button onClick={() => navigate('/medical-history/add')}>
          <Plus className="h-4 w-4 mr-1" />
          {t('addNewButton', { ns: 'medicalHistory' })}
        </Button>
      </div>

      {/* Year Filter */}
      <YearSelector
        years={availableYears}
        selectedYear={selectedYear}
        onChange={setSelectedYear}
      />

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive font-medium">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredHistories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredHistories.map((record) => (
            <Card key={record.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-emerald-100 p-2.5 mt-0.5">
                    <ClipboardList className="h-5 w-5 text-emerald-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate">{getChildName(record.child)}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('yearLabel', { ns: 'medicalHistory' })}: {record.year}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm" onClick={() => handleViewClick(record)}>
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        {t('view', { ns: 'common' })}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(record.id)}>
                        <Download className="h-3.5 w-3.5 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <ClipboardList className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">{t('noRecords', { ns: 'medicalHistory' })}</p>
            <Button onClick={() => navigate('/medical-history/add')}>
              <Plus className="h-4 w-4 mr-1" />
              {t('addNewButton', { ns: 'medicalHistory' })}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* View Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedRecord && getChildName(selectedRecord.child)} - {selectedRecord?.year}
            </DialogTitle>
            <DialogDescription className="sr-only">Medical history details</DialogDescription>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-1">
              <div className="flex gap-2 mb-4">
                <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(selectedRecord.id)}>
                  <Download className="h-3.5 w-3.5 mr-1" />
                  {t('downloadPdfButton', { ns: 'medicalHistory', defaultValue: 'Download PDF' })}
                </Button>
              </div>

              {renderSection(t('sections.basicInformation', { ns: 'medicalHistory' }), (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><span className="font-medium">{t('childLabel', { ns: 'medicalHistory' })}:</span> {getChildName(selectedRecord.child)}</div>
                  <div><span className="font-medium">{t('yearLabel', { ns: 'medicalHistory' })}:</span> {selectedRecord.year}</div>
                  <div><span className="font-medium">{t('weightLabel', { ns: 'medicalHistory' })}:</span> {selectedRecord.weight} kg</div>
                  <div><span className="font-medium">{t('bloodTypeLabel', { ns: 'medicalHistory' })}:</span> {selectedRecord.blood_type}</div>
                </div>
              ))}

              {renderSection(t('sections.vaccination', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.isVaccinated', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.is_vaccinated)}</p>
                  {!selectedRecord.is_vaccinated && selectedRecord.missing_vaccines && (
                    <p className="mt-2">{t('form.missingVaccines', { ns: 'medicalHistory' })}: {renderArrayData(selectedRecord.missing_vaccines)}</p>
                  )}
                </div>
              ))}

              {renderSection(t('sections.knownDiseases', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.hasKnownDisease', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.has_any_known_disease)}</p>
                  {selectedRecord.has_any_known_disease && <p className="mt-2">{renderArrayData(selectedRecord.known_diseases)}</p>}
                </div>
              ))}

              {renderSection(t('sections.allergies', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.hasAllergies', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.has_allergies)}</p>
                  {selectedRecord.has_allergies && <p className="mt-2">{renderArrayData(selectedRecord.allergies)}</p>}
                </div>
              ))}

              {renderSection(t('sections.majorDisease', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.hasMajorDisease', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.has_any_major_disease)}</p>
                  {selectedRecord.has_any_major_disease && <p className="mt-2">{renderArrayData(selectedRecord.major_disease)}</p>}
                </div>
              ))}

              {renderSection(t('sections.medication', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.isReceivingMedication', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.is_receiving_medication)}</p>
                  {selectedRecord.is_receiving_medication && <p className="mt-2">{renderArrayData(selectedRecord.medication)}</p>}
                </div>
              ))}

              {renderSection(t('sections.psychologicalDisorders', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.hasPsychologicalDisorder', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.has_psychological_disorder)}</p>
                  {selectedRecord.has_psychological_disorder && <p className="mt-2">{renderArrayData(selectedRecord.psychological_disorders)}</p>}
                </div>
              ))}

              {renderSection(t('sections.developmentDisorders', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.hasDevelopmentDisorder', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.has_any_development_disorder)}</p>
                  {selectedRecord.has_any_development_disorder && <p className="mt-2">{renderArrayData(selectedRecord.development_disorders)}</p>}
                </div>
              ))}

              {renderSection(t('sections.specialistCare', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.isWatchedBySpecialist', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.is_being_watched_by_specialist)}</p>
                  {selectedRecord.is_being_watched_by_specialist && <p className="mt-2">{renderArrayData(selectedRecord.specialist_contact_info)}</p>}
                </div>
              ))}

              {renderSection(t('sections.riskGroup', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.belongsToRiskGroup', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.belongs_to_risk_group)}</p>
                  {selectedRecord.belongs_to_risk_group && <p className="mt-2">{renderArrayData(selectedRecord.risk_group_explanation)}</p>}
                </div>
              ))}

              {renderSection(t('sections.underlyingDiseases', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.hasUnderlyingDisease', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.has_underlying_disease)}</p>
                  {selectedRecord.has_underlying_disease && <p className="mt-2">{renderArrayData(selectedRecord.underlying_diseases)}</p>}
                </div>
              ))}

              {renderSection(t('sections.healthIssues', { ns: 'medicalHistory' }), (
                <div>
                  <p>{t('form.hasHealthIssues', { ns: 'medicalHistory' })}: {renderYesNo(selectedRecord.has_worth_mentioning_health_issues)}</p>
                  {selectedRecord.has_worth_mentioning_health_issues && <p className="mt-2">{renderArrayData(selectedRecord.worth_mentioning_health_issues)}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
